import React from 'react'
import classNames from 'classnames'
import {
  FooterNavGroupFragment,
  GetFooterContentQuery,
} from '@dustin-web/microsite-data/src/graphql/content/generated/graphql-types'
import { GET_FOOTER_CONTENT } from '@dustin-web/microsite-data/src/graphql/content/get-footer-content.graphql'
import DustinLogo from '@dustin-web/microsite-components/src/dustin-logo'
import { useResponsive, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import { Accordion } from '@dustin-web/microsite-components'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'
import { useSiteContext } from '@dustin-web/microsite-components/src/hooks'
import FooterAccountSignup from './footer-parts/footer-account-signup'
import FooterNewsletterSignup from './footer-parts/footer-newsletter-signup'
import { FooterDeliveryPaymentsContainer } from './footer-parts/footer-delivery-payments-container'
import { useAuth } from '@dustin-web/microsite-components/src/auth'
import { WindowBreakpoints } from '@dustin-web/microsite-config/src/breakpoints'
import Image from 'next/image'

const MEDIA_CONTENT_PATH = 'https://media.dustin.eu/content/'

const SOCIAL_MEDIA_IMAGE_URL = {
  facebook: `${MEDIA_CONTENT_PATH}88934/facebook.svg`,
  instagram: `${MEDIA_CONTENT_PATH}88935/instagram.svg`,
  youTube: `${MEDIA_CONTENT_PATH}88939/youtube.svg`,
  twitter: `${MEDIA_CONTENT_PATH}88940/twitter.svg`,
  linkedIn: `${MEDIA_CONTENT_PATH}88936/linkedin.svg`,
}

type socialMediaLinks = {
  facebook?: string
  instagram?: string
  linkedIn?: string
  twitter?: string
  youTube?: string
}

const FooterContainer = () => {
  const { isSmallScreen } = useResponsive()
  const { site } = useSiteContext()
  const { breakpoints } = useResponsive()
  const { isAuthenticated, isLoading: isLoadingAuth } = useAuth()

  const {
    data,
    isValidating: loading,
    error,
  } = useGraphql<GetFooterContentQuery>(GET_FOOTER_CONTENT, {})

  if (error) {
    throw error
  }

  if (!data && loading) {
    return null
  }

  const contactInfo = data?.footer?.contactInfo
  const companyInfo = data?.footer?.companyInfo
  const navGroups = data?.footer?.navigationGroups
  const socialMediaLinks = data?.footer?.socialMediaLinks as socialMediaLinks
  const socialMediaCollection = Object.entries(socialMediaLinks)
  const currentYear = new Date().getFullYear()

  return (
    <Footer
      contactInfo={contactInfo}
      companyInfo={companyInfo}
      socialMediaLinks={socialMediaCollection}
      navGroups={navGroups}
      currentYear={currentYear}
      isSmallScreen={isSmallScreen}
      site={site}
      breakpoints={breakpoints}
      isAuthenticated={isAuthenticated}
      isLoadingAuth={isLoadingAuth}
    />
  )
}

type FooterProps = {
  contactInfo: string | null | undefined
  companyInfo: string | undefined
  socialMediaLinks?: Array<[string, string]>
  navGroups: any
  currentYear: number
  isSmallScreen: boolean
  site: string
  breakpoints: WindowBreakpoints
  isAuthenticated: boolean
  isLoadingAuth: boolean
}

const Footer = ({
  contactInfo,
  companyInfo,
  socialMediaLinks,
  navGroups,
  currentYear,
  isSmallScreen,
  site,
  breakpoints,
  isAuthenticated,
  isLoadingAuth,
}: FooterProps) => (
  <footer id="footer" className={'c-footer business-site'}>
    <nav aria-label="footer">
      <div className="c-box c-box--section t-gray-10 u-px-sm-24 u-pt-32 u-pt-md-48 u-pb-48 u-w-100">
        <div className={`container grid-container u-px-16 ${site}`}>
          <div>
            {!isLoadingAuth &&
              (!isAuthenticated ? <FooterAccountSignup /> : <FooterNewsletterSignup />)}
            {contactInfo && breakpoints.min.MD && (
              <div
                className="row u-overflow-hidden u-pt-48 u-w-100"
                dangerouslySetInnerHTML={{
                  __html: contactInfo.replace(/<img/g, '<img loading="lazy"'),
                }}
              />
            )}
          </div>
          <div className="wrapper-icons">
            <FooterDeliveryPaymentsContainer />
          </div>
        </div>
        <div className="container">
          {contactInfo && breakpoints.max.MD && (
            <div
              className="row u-overflow-hidden u-pt-32 u-w-100"
              dangerouslySetInnerHTML={{
                __html: contactInfo.replace(/<img/g, '<img loading="lazy"'),
              }}
            />
          )}
        </div>
      </div>
      <div className="c-box c-box--section t-gray-09 u-pt-0 u-pt-lg-48 u-pb-16 u-pb-lg-80">
        {isSmallScreen ? (
          <>
            {navGroups &&
              navGroups.map((group: any, key: number) => (
                <Accordion
                  key={group.header + key}
                  title={group.header}
                  className={'u-color-white'}>
                  <FooterGroupList group={group} withChatLink={key === 1} />
                </Accordion>
              ))}
          </>
        ) : (
          <div className="container">
            <div className="row">
              {navGroups &&
                navGroups.map((group: any, key: number) => (
                  <div className="col-md-3" key={group.header + key}>
                    <h4 className="h4 u-mb-8 u-color-white u-fw-normal">{group.header}</h4>
                    <FooterGroupList group={group} withChatLink={key === 1} />
                  </div>
                ))}
            </div>
          </div>
        )}
        <div className="container u-pt-32 u-px-16 u-px-lg-0 u-pt-md-64">
          <div className="row sm-gutters">
            <div className="col-12 col-md-6 d-flex">
              <div className="u-pr-16 d-md-block">
                <DustinLogo
                  className="c-footer__logo"
                  onlySymbol
                  style={{ width: '49px', height: '52px', fill: '#FFFFFF' }}
                />
              </div>
              {companyInfo && (
                <div>
                  <div dangerouslySetInnerHTML={{ __html: companyInfo }}></div>
                  <p>&copy; {currentYear} Dustin Group AB. All rights reserved.</p>
                </div>
              )}
            </div>
            <div className="col-12 col-md-6 d-flex align-items-center justify-content-center justify-content-md-end u-pt-32 u-pt-md-0">
              {socialMediaLinks?.map((item, key) => {
                const socialMediaName = item[0]
                const imageLink = item[1]
                return (
                  <SocialMediaLinkImage
                    key={key + '-' + socialMediaName}
                    link={{
                      href: imageLink,
                      ariaLabel: `follow on ${socialMediaName}`,
                    }}
                    image={{
                      alt: `${socialMediaName} icon`,
                      src: SOCIAL_MEDIA_IMAGE_URL[socialMediaName as keyof socialMediaLinks],
                    }}
                  />
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </nav>
  </footer>
)

type SocialMediaLinkImageProps = {
  link: {
    href: string | undefined
    ariaLabel: string
  }
  image: {
    src: string
    alt: string
  }
  last?: boolean
}

const SocialMediaLinkImage = ({ link, image, last }: SocialMediaLinkImageProps) => {
  if (!link.href) return null
  return (
    <a
      className={classNames('u-bg-black u-w-40 u-h-40 u-py-8 u-px-8 u-radius-4 u-button-opacity', {
        'u-mr-8': !last,
      })}
      aria-label={link.ariaLabel}
      href={link.href}
      target="_blank"
      rel="noopener noreferrer">
      <Image src={image.src} alt={image.alt} width={24} height={24} />
    </a>
  )
}

type FooterGroupListType = {
  group: FooterNavGroupFragment
  withChatLink?: boolean
}

const FooterGroupList = ({ group, withChatLink = false }: FooterGroupListType) => {
  const openZendeskChat = () => {
    const event = new Event('openZendeskChat')
    window.dispatchEvent(event)
  }
  const { translate } = useTranslations()
  return (
    <ul className="ul-stripped">
      {group.links.map((link, key) => (
        <React.Fragment key={link.text + key}>
          <li className="u-mb-8">
            <a
              className="a-stripped"
              href={link.url}
              target={link.openInNewWindow ? '_blank' : '_self'}
              rel="noreferrer">
              {link.text}
            </a>
          </li>
          {withChatLink && key === group.links.length - 1 && (
            <li className="u-mb-8">
              <a
                className="a-stripped"
                data-open-chat="true"
                style={{ cursor: 'pointer' }}
                onClick={openZendeskChat}>
                {translate('ChatWithUs')}
              </a>
            </li>
          )}
        </React.Fragment>
      ))}
    </ul>
  )
}

export default FooterContainer
